"use client";

import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { Service } from "@/Types/responses/service";

interface ServiceCardProps {
    service: Service;
    isHovered: boolean;
    onHover: () => void;
}

export default function ServiceCard({ service, isHovered, onHover }: ServiceCardProps) {
    return (
        <Link
            href={`/our-services/${service.serviceSlug}`}
            onMouseEnter={onHover}
            style={{ flexGrow: isHovered ? 4 : 1 }}
            className="relative flex h-full min-w-0 overflow-hidden rounded-2xl bg-black transition-[flex-grow] duration-500 ease-in-out"
        >
            {service.featuredImage?.url && (
                <Image
                    src={service.featuredImage.url}
                    alt={service.serviceTitle}
                    fill
                    className="object-cover object-center"
                />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

            <span
                className={`absolute right-5 top-5 z-10 inline-flex items-center justify-center rounded-full bg-secondary p-3 text-black transition-transform duration-500 ${
                    isHovered ? "rotate-45" : "rotate-0"
                }`}
            >
                <ArrowRight className="h-4 w-4 -rotate-45 shrink-0" />
            </span>

            {/* CLOSED: vertical rotated title */}
            {!isHovered && (
                <div className="relative z-10 flex w-full items-end justify-center pb-8">
                    <h3 className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap text-lg font-semibold tracking-wide text-white md:text-xl">
                        {service.serviceTitle}
                    </h3>
                </div>
            )}

            {/* OPEN: horizontal title + description */}
            {isHovered && (
                <div className="relative z-10 flex w-full flex-col justify-end gap-2 p-6 md:p-8">
                    <h3 className="whitespace-nowrap text-2xl font-bold text-white md:text-3xl">
                        {service.serviceTitle}
                    </h3>
                    <p className="max-w-md text-sm leading-relaxed text-white/80 md:text-base">
                        {service.metaDescription}
                    </p>
                </div>
            )}
        </Link>
    );
}